/**
 * MIDI input with MPE (MIDI Polyphonic Expression) and polyphonic aftertouch.
 *
 * An MPE controller (ROLI Seaboard/Lumi, LinnStrument, Osmose, Push 3, …)
 * plays every note on its own "member" channel, so pitch bend, pressure
 * (channel pressure) and timbre (CC74) on that channel belong to that one
 * note. The zone's "master" channel carries messages for the whole
 * instrument. This turns raw MIDI bytes into note, per-note expression and
 * global actions; it keeps no audio state and is easy to test.
 *
 * MPE switches on by itself when the controller sends the MPE Configuration
 * Message (RPN 6 on a master channel), and can also be forced on or off.
 *
 * Without MPE, polyphonic aftertouch (0xA0) still reaches each note as
 * per-note pressure, and every channel's bend is global, as before.
 */

export const MPE_DEFAULT_BEND_RANGE = 48;

const NOTE_OFF = 0x80;
const NOTE_ON = 0x90;
const POLY_PRESSURE = 0xa0;
const CONTROL = 0xb0;
const CHANNEL_PRESSURE = 0xd0;
const PITCH_BEND = 0xe0;

export const noteSourceId = (channel, note) => `midi:${channel}:${note}`;

const channelState = () => ({ bend: 0, pressure: 0, timbre: 0.5, notes: new Set(), rpn: [127, 127] });

export class MpeInput {
  constructor({ enabled = 'auto', bendRange = MPE_DEFAULT_BEND_RANGE } = {}) {
    // 'auto' follows the controller's MCM; true / false override it.
    this.mode = enabled;
    this.bendRange = bendRange;
    this.zone = null; // { master: 0 | 15, members: n } once announced
    this.channels = Array.from({ length: 16 }, channelState);
  }

  get active() {
    if (this.mode === true) return true;
    if (this.mode === false) return false;
    return Boolean(this.zone && this.zone.members > 0);
  }

  setMode(mode) {
    this.mode = mode === true || mode === false ? mode : 'auto';
  }

  setBendRange(semitones) {
    const value = Number(semitones);
    if (Number.isFinite(value)) this.bendRange = Math.max(1, Math.min(96, value));
  }

  /** The zone's master channel: announced, or channel 1 when MPE is forced on. */
  get masterChannel() {
    return this.zone?.master ?? 0;
  }

  isMember(channel) {
    return this.active && channel !== this.masterChannel;
  }

  /** Translate one MIDI message into actions. */
  handle(data) {
    const [status = 0, first = 0, second = 0] = data || [];
    const command = status & 0xf0;
    const channel = status & 0x0f;
    const state = this.channels[channel];
    const member = this.isMember(channel);
    const actions = [];

    if (command === NOTE_ON && second > 0) {
      const id = noteSourceId(channel, first);
      state.notes.add(id);
      actions.push({ type: 'noteOn', note: first, velocity: second / 127, id });
      // The finger may already be bending or pressing when the note starts.
      if (member) {
        actions.push({
          type: 'expression', id,
          pitch: state.bend * this.bendRange, pressure: state.pressure, timbre: state.timbre
        });
      }
    } else if (command === NOTE_OFF || command === NOTE_ON) {
      const id = noteSourceId(channel, first);
      state.notes.delete(id);
      actions.push({ type: 'noteOff', note: first, id });
      if (member && state.notes.size === 0) {
        // The next note on this channel starts from rest.
        state.bend = 0;
        state.pressure = 0;
      }
    } else if (command === PITCH_BEND) {
      const bend = (((second << 7) | first) - 8192) / 8192;
      if (member) {
        state.bend = bend;
        state.notes.forEach((id) => actions.push({ type: 'expression', id, pitch: bend * this.bendRange }));
      } else {
        actions.push({ type: 'pitchBend', value: bend });
      }
    } else if (command === CHANNEL_PRESSURE) {
      const pressure = first / 127;
      if (member) {
        state.pressure = pressure;
        state.notes.forEach((id) => actions.push({ type: 'expression', id, pressure }));
      } else {
        actions.push({ type: 'aftertouch', value: pressure });
      }
    } else if (command === POLY_PRESSURE) {
      actions.push({ type: 'expression', id: noteSourceId(channel, first), pressure: second / 127 });
    } else if (command === CONTROL) {
      this._control(channel, first, second, actions);
    }
    return actions;
  }

  _control(channel, controller, value, actions) {
    const state = this.channels[channel];
    if (controller === 101) state.rpn[0] = value;
    else if (controller === 100) state.rpn[1] = value;
    else if (controller === 6 && state.rpn[0] === 0 && state.rpn[1] === 6 && (channel === 0 || channel === 15)) {
      // MPE Configuration Message: value = member channel count, 0 = off.
      this.zone = value > 0 ? { master: channel, members: value } : null;
      actions.push({ type: 'mpe', active: this.active, zone: this.zone });
      return;
    } else if (controller === 6 && state.rpn[0] === 0 && state.rpn[1] === 0 && this.isMember(channel)) {
      // Pitch-bend sensitivity (RPN 0) on a member channel sets the MPE range.
      this.setBendRange(value);
      return;
    }
    if (controller === 74 && this.isMember(channel)) {
      state.timbre = value / 127;
      state.notes.forEach((id) => actions.push({ type: 'expression', id, timbre: state.timbre }));
      return;
    }
    if (controller === 64) actions.push({ type: 'sustain', down: value >= 64 });
    else if (controller === 1) actions.push({ type: 'modWheel', value: value / 127 });
    else if (controller === 120 || controller === 123) actions.push({ type: 'allNotesOff', immediate: controller === 120 });
  }
}
